/**
 * Client-state lifecycle: ONE place that owns versioning + expiry + migration
 * for every localStorage key the SPA persists (outstanding-qbo.10).
 *
 * Why this exists: the persisted keys historically had NO versioning, NO
 * expiry, and NO migration story, and three instances already bit — the
 * formerly-immortal experiment-assignments map (kill-switch bug, qbo.1),
 * legacy carts without optionIds 400ing the address step until manually
 * cleared, and first-touch attribution never expiring (a long-lived visitor's
 * channel attribution was first-touch-FOREVER).
 *
 * Every value is stored in a versioned envelope:
 *
 *   { v: number, at: number, data: T }
 *
 * where `v` is the key's schema version and `at` is the epoch-ms write time
 * (the expiry clock for keys with a TTL). Reads are tolerant by contract:
 *  - a pre-envelope (legacy raw) value migrates on read when the key defines
 *    `migrateLegacy`, and the migrated value is written back enveloped;
 *  - an OLDER envelope version migrates via `migrateVersion` (or is discarded);
 *  - an UNKNOWN (newer) version is discarded — a rolled-back build must never
 *    crash on a value written by a newer one;
 *  - corrupt JSON / failed validation clears the key.
 * Nothing here ever throws (private mode / SSR / quota) — reads resolve to
 * `null` and writes report success as a boolean.
 *
 * Key census + policy (verified 2026-07-05; there is no persisted "theme" key
 * in this codebase — the census is these seven):
 *
 *   cart                     versioned + migrated; legacy items that cannot
 *                            check out (no optionIds) are dropped rather than
 *                            400ing the address step
 *   favorites                versioned, no expiry
 *   visitor_id               permanent by design (versioned, never expires)
 *   experiment_assignments   versioned; overwritten on every SUCCESSFUL
 *                            assignments fetch (wave-1 semantics live in
 *                            events.ts setExperimentAssignments)
 *   first_touch_attribution  90-day expiry (OWNER-DECIDED 2026-07-05): the
 *                            clock counts QUIET days — every app load
 *                            refreshes `at`, so attribution expires only
 *                            after 90 days of NO visits, and the next touch
 *                            becomes the new first touch
 *   promo_banner:{key}:{variant}  dismissals, 90-day TTL
 *   cookie-consent           versioned, no expiry
 *
 * DELIBERATELY OUT OF SCOPE — the sessionStorage keys `checkout-order`,
 * `checkout-redirect` (useCheckout), and `exposed_experiments` (events.ts).
 * They are per-TAB and ephemeral: they die with the tab, so there is nothing to
 * version, migrate, or expire — a versioned localStorage envelope would be
 * over-engineering. This module owns durable, cross-load localStorage only
 * (bead bjfq, decided 2026-07-04).
 *
 * @format
 */

import { ATTRIBUTION_VALUE_MAX_LENGTH } from "@outstanding/shared";
import type { CartLineItem, RuntimeCart } from "@/composables/useCart";
import type { FavoritePhoto } from "@/utils/favorites";
import type { Attribution } from "@/utils/events";

/** The versioned wrapper every value is persisted inside. */
export interface Envelope<T> {
  v: number;
  at: number;
  data: T;
}

/**
 * Per-key lifecycle policy. `decode` validates/normalizes CURRENT-version
 * data (return undefined to discard); `migrateLegacy` upgrades a pre-envelope
 * raw value (it receives the JSON-parsed value when the stored string parsed,
 * else the raw string itself); `migrateVersion` upgrades an older envelope's
 * data. Anything unmigratable is cleared — never crashes, never reaches a
 * consumer malformed.
 */
export interface KeyPolicy<T> {
  key: string;
  version: number;
  /** Values older than this (per the envelope's `at`) are expired + cleared. */
  ttlMs?: number;
  decode: (data: unknown) => T | undefined;
  migrateLegacy?: (raw: unknown) => { data: T; at?: number } | undefined;
  migrateVersion?: (fromVersion: number, data: unknown) => T | undefined;
}

export const DAY_MS = 24 * 60 * 60 * 1000;
/** Attribution + promo-dismissal TTL (owner-decided 2026-07-05). */
export const NINETY_DAYS_MS = 90 * DAY_MS;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isEnvelope(value: unknown): value is Envelope<unknown> {
  return (
    isPlainObject(value) &&
    typeof value.v === "number" &&
    typeof value.at === "number" &&
    "data" in value
  );
}

/** Expired when the value's age exceeds the policy's TTL (age === TTL is still fresh). */
function isExpired(policy: KeyPolicy<unknown>, at: number): boolean {
  return policy.ttlMs !== undefined && Date.now() - at > policy.ttlMs;
}

function writeEnvelope<T>(policy: KeyPolicy<T>, data: T, at: number): boolean {
  try {
    if (typeof localStorage === "undefined") return false;
    const envelope: Envelope<T> = { v: policy.version, at, data };
    localStorage.setItem(policy.key, JSON.stringify(envelope));
    return true;
  } catch {
    // Quota / private mode — persistence is best-effort.
    return false;
  }
}

/** Remove a key. Never throws. */
export function removeClientState(policy: Pick<KeyPolicy<unknown>, "key">): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.removeItem(policy.key);
  } catch {
    // ignore
  }
}

/**
 * Persist `data` under the policy's key in a fresh envelope (at = now).
 * Returns whether the write actually landed, so callers that must not claim
 * durable state (e.g. first-touch capture) can tell. Never throws.
 */
export function writeClientState<T>(policy: KeyPolicy<T>, data: T): boolean {
  return writeEnvelope(policy, data, Date.now());
}

/**
 * Read the policy's key: unwrap + validate the envelope, migrate legacy/older
 * values (writing the upgraded envelope back), enforce expiry, and clear
 * anything corrupt or unknown. Returns null when absent/expired/unusable.
 * Never throws.
 */
export function readClientState<T>(policy: KeyPolicy<T>): T | null {
  try {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(policy.key);
    if (raw === null) return null;

    let parsed: unknown;
    let parseFailed = false;
    try {
      parsed = JSON.parse(raw);
    } catch {
      parseFailed = true;
    }

    if (!parseFailed && isEnvelope(parsed)) {
      if (parsed.v === policy.version) {
        if (isExpired(policy, parsed.at)) {
          removeClientState(policy);
          return null;
        }
        const data = policy.decode(parsed.data);
        if (data === undefined) {
          removeClientState(policy);
          return null;
        }
        return data;
      }
      if (parsed.v < policy.version && policy.migrateVersion) {
        if (isExpired(policy, parsed.at)) {
          removeClientState(policy);
          return null;
        }
        const data = policy.migrateVersion(parsed.v, parsed.data);
        if (data === undefined) {
          removeClientState(policy);
          return null;
        }
        // Keep the ORIGINAL `at` — a version migration must not extend a TTL.
        writeEnvelope(policy, data, parsed.at);
        return data;
      }
      // Unknown (newer) version, or an older one with no migration: discard —
      // guessing at a foreign schema is how downstream 400s happen.
      removeClientState(policy);
      return null;
    }

    // Pre-envelope legacy value: hand the parsed JSON (or, when the stored
    // string was not valid JSON — e.g. a bare visitor id — the raw string) to
    // the key's legacy migration.
    const migrated = policy.migrateLegacy?.(parseFailed ? raw : parsed);
    if (!migrated) {
      removeClientState(policy);
      return null;
    }
    const at = migrated.at ?? Date.now();
    if (isExpired(policy, at)) {
      removeClientState(policy);
      return null;
    }
    // Write the upgraded envelope back so the migration runs exactly once.
    writeEnvelope(policy, migrated.data, at);
    return migrated.data;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Per-key policies
// ---------------------------------------------------------------------------

/**
 * One persisted cart line item, vetted for checkout viability: the API's
 * items PUT requires >= 1 numeric optionId per line (cart.ts zod bound,
 * `min(1)`), so an item without them 400s the address step — the exact
 * legacy-cart failure this bead retires. Items added by the current build
 * always carry optionIds (Detail.vue gates Add-to-cart on a full option
 * selection); anything else is unpurchasable and is dropped so the customer
 * sees an accurate (possibly empty) cart instead of a checkout error.
 */
function decodeCartItem(raw: unknown): CartLineItem | undefined {
  if (!isPlainObject(raw)) return undefined;
  const { id, lineItemId, subtotal, optionIds, selected } = raw;
  if (typeof id !== "number" || !Number.isFinite(id)) return undefined;
  if (typeof lineItemId !== "string" && typeof lineItemId !== "number") return undefined;
  if (typeof subtotal !== "number" || !Number.isFinite(subtotal)) return undefined;
  if (
    !Array.isArray(optionIds) ||
    optionIds.length === 0 ||
    !optionIds.every((o) => typeof o === "number" && Number.isFinite(o))
  ) {
    return undefined;
  }
  return {
    ...raw,
    id,
    lineItemId,
    subtotal,
    optionIds,
    selected: Array.isArray(selected) ? selected : [],
  } as CartLineItem;
}

function decodeCart(data: unknown): RuntimeCart | undefined {
  if (!isPlainObject(data) || !Array.isArray(data.items)) return undefined;
  const items = data.items
    .map(decodeCartItem)
    .filter((item): item is CartLineItem => item !== undefined);
  return { items };
}

/**
 * cart — versioned + migrated; NEVER silently swallowed wholesale. A legacy
 * cart migrates item-by-item: lines that can still check out survive, lines
 * without recoverable optionIds cannot (the display strings in `selected`
 * don't map back to option ids), so they are dropped — an empty cart beats a
 * 400 at the address step.
 */
export const cartPolicy: KeyPolicy<RuntimeCart> = {
  key: "cart",
  version: 1,
  decode: decodeCart,
  migrateLegacy: (raw) => {
    const cart = decodeCart(raw);
    return cart ? { data: cart } : undefined;
  },
};

function decodeFavorites(data: unknown): FavoritePhoto[] | undefined {
  if (!Array.isArray(data)) return undefined;
  const favorites: FavoritePhoto[] = [];
  for (const entry of data) {
    if (!isPlainObject(entry)) continue;
    if (typeof entry.slug !== "string" || !entry.slug) continue;
    favorites.push({
      slug: entry.slug,
      title: typeof entry.title === "string" ? entry.title : "",
      ...(typeof entry.alt_text === "string" ? { alt_text: entry.alt_text } : {}),
      ...(typeof entry.thumbnailUrl === "string" ? { thumbnailUrl: entry.thumbnailUrl } : {}),
      ...(typeof entry.imageUrl === "string" ? { imageUrl: entry.imageUrl } : {}),
    });
  }
  return favorites;
}

/** favorites — versioned, no expiry (a wishlist has no natural staleness). */
export const favoritesPolicy: KeyPolicy<FavoritePhoto[]> = {
  key: "favorites",
  version: 1,
  decode: decodeFavorites,
  migrateLegacy: (raw) => {
    const favorites = decodeFavorites(raw);
    return favorites ? { data: favorites } : undefined;
  },
};

function decodeVisitorId(data: unknown): string | undefined {
  return typeof data === "string" && data.trim() !== "" ? data : undefined;
}

/**
 * visitor_id — PERMANENT by design (versioned, never expires): it seeds
 * experiment bucketing and session funnels, so a returning visitor must never
 * lose it. The legacy value is a bare (non-JSON) uuid string; migration keeps
 * it byte-for-byte.
 */
export const visitorIdPolicy: KeyPolicy<string> = {
  key: "visitor_id",
  version: 1,
  decode: decodeVisitorId,
  migrateLegacy: (raw) => {
    const id = decodeVisitorId(raw);
    return id ? { data: id } : undefined;
  },
};

function decodeAssignmentMap(data: unknown): Record<string, string> | undefined {
  if (!isPlainObject(data)) return undefined;
  const map: Record<string, string> = {};
  for (const [key, value] of Object.entries(data)) {
    if (typeof value === "string") map[key] = value;
  }
  return map;
}

/**
 * experiment_assignments — versioned, no expiry; freshness comes from being
 * OVERWRITTEN on every successful assignments fetch (the wave-1 kill-switch
 * fix — see events.ts setExperimentAssignments, which persists EXACTLY the
 * fetch-derived map, {} included, while a FAILED fetch keeps the stale map).
 * Legacy raw `{ key: variant }` maps migrate on read so pre-envelope visitors
 * keep their bucket.
 */
export const assignmentsPolicy: KeyPolicy<Record<string, string>> = {
  key: "experiment_assignments",
  version: 1,
  decode: decodeAssignmentMap,
  migrateLegacy: (raw) => {
    const map = decodeAssignmentMap(raw);
    return map ? { data: map } : undefined;
  },
};

const ATTRIBUTION_FIELDS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "referrer",
  "landing_path",
] as const;

function decodeAttribution(data: unknown): Attribution | undefined {
  if (!isPlainObject(data)) return undefined;
  const attribution: Attribution = {};
  for (const field of ATTRIBUTION_FIELDS) {
    const value = data[field];
    if (typeof value === "string" && value) {
      // The SAME cap the API's ingest truncates to (routes/events.ts) and the
      // cart's zod schema enforces — one shared constant, never a local copy,
      // so a future cap change cannot leave the client persisting values the
      // server will reject (bead outstanding-9rgp.9).
      attribution[field] = value.slice(0, ATTRIBUTION_VALUE_MAX_LENGTH);
    }
  }
  // An attribution with no recognizable field is noise, not a first touch —
  // discard it so a real attributable landing can still capture.
  return Object.keys(attribution).length > 0 ? attribution : undefined;
}

/**
 * first_touch_attribution — 90-DAY expiry (OWNER-DECIDED 2026-07-05). The
 * clock counts QUIET days: captureFirstTouchAttribution refreshes `at` on
 * every app load, so an actively returning visitor keeps their original
 * first touch while 90 days of silence lets the next touch become the new
 * first touch. This retires the live bug where any long-lived browser's
 * channel attribution was first-touch-forever.
 */
export const attributionPolicy: KeyPolicy<Attribution> = {
  key: "first_touch_attribution",
  version: 1,
  ttlMs: NINETY_DAYS_MS,
  decode: decodeAttribution,
  migrateLegacy: (raw) => {
    const attribution = decodeAttribution(raw);
    // No legacy timestamp exists, so the 90-day clock starts at migration.
    return attribution ? { data: attribution } : undefined;
  },
};

type ConsentValue = "granted" | "denied";

function decodeConsent(data: unknown): ConsentValue | undefined {
  return data === "granted" || data === "denied" ? data : undefined;
}

/** cookie-consent — versioned, no expiry (a consent choice does not lapse). */
export const consentPolicy: KeyPolicy<ConsentValue> = {
  key: "cookie-consent",
  version: 1,
  decode: decodeConsent,
  migrateLegacy: (raw) => {
    // Legacy value is the bare (non-JSON) string "granted" | "denied".
    const choice = decodeConsent(raw);
    return choice ? { data: choice } : undefined;
  },
};

// A legacy promo dismissal stored String(Date.now()); treat any parsed value
// in a plausible epoch-ms window as the original dismissal time so its TTL
// clock is honest, and anything else as "dismissed just now".
const PLAUSIBLE_EPOCH_MS_MIN = Date.UTC(2020, 0, 1);

function decodeDismissedAt(data: unknown): number | undefined {
  return typeof data === "number" && Number.isFinite(data) && data > 0 ? data : undefined;
}

/**
 * promo_banner:{experimentKey}:{variant} — per-campaign dismissals with a
 * 90-day TTL: a campaign the visitor closed stays closed for 90 days, then
 * may reappear (long-running campaigns are re-pitched, not suppressed
 * forever). Dynamic key names, hence a policy factory.
 */
export function promoDismissalPolicy(storageKey: string): KeyPolicy<number> {
  return {
    key: storageKey,
    version: 1,
    ttlMs: NINETY_DAYS_MS,
    decode: decodeDismissedAt,
    migrateLegacy: (raw) => {
      const numeric = typeof raw === "number" ? raw : Number(raw);
      const plausible =
        Number.isFinite(numeric) &&
        numeric >= PLAUSIBLE_EPOCH_MS_MIN &&
        numeric <= Date.now() + DAY_MS;
      const dismissedAt = plausible ? numeric : Date.now();
      // ANY legacy value under this key means "the visitor dismissed it".
      return { data: dismissedAt, at: dismissedAt };
    },
  };
}
